import type { Colour, PointSystem, RoundEntry, Score } from '../model/types.js';

/** A player as a pairing engine sees them. */
export interface PairingPlayer {
  /** Pairing number (TPN), 1-based. */
  readonly id: number;
  readonly rating: number;
  /** Rounds before the one being paired (index 0 = round 1). */
  readonly history: readonly RoundEntry[];
  /** False when the player is withdrawn, absent or has a requested bye this round. */
  readonly available: boolean;
  /** Extra virtual points (tenths) for pairing this round (acceleration). */
  readonly acceleration?: Score;
  /** Virtual points the player had in each earlier round (index 0 = round 1). */
  readonly accelerationHistory?: readonly Score[];
}

export interface PairingRequest {
  /** The round to pair, 1-based. */
  readonly round: number;
  readonly totalRounds: number;
  readonly players: readonly PairingPlayer[];
  readonly pointSystem: PointSystem;
  /** Colour of the top-ranked player in round 1. */
  readonly initialColour: Colour;
  /** Pairs of pairing numbers that must not meet this round (beyond rematches). */
  readonly forbiddenPairs?: readonly (readonly [number, number])[];
}

export interface Pair {
  readonly white: number;
  readonly black: number;
}

export interface PairingResult {
  /** Pairs in board order. */
  readonly pairs: readonly Pair[];
  /** Players given the pairing-allocated bye (usually zero or one). */
  readonly byes: readonly number[];
  /** Human-readable notes (for example relaxed criteria). */
  readonly notes: readonly string[];
  /**
   * Players given a half-point bye by the system itself (Double-Swiss: the
   * second game of a pairing-allocated bye, so the bye is worth a win and a
   * draw).
   */
  readonly halfPointByes?: readonly number[];
  /** Plain-language account of how the pairing was reached (engines that support it). */
  readonly explanation?: readonly string[];
}

export class PairingError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PairingError';
  }
}

export interface PairingEngine {
  readonly id: string;
  readonly name: string;
  pair(request: PairingRequest): PairingResult;
}
